import React from 'react';
import { useDrag } from 'react-use-gesture';

export type SlideDirection = 'left' | 'right';

export interface SlideGestureProps {
  readonly enabled: boolean;
  readonly sliderWidth: number;
  readonly dragThreshold: number;
  readonly onDrag: (relativeDelta: number) => void;
  readonly onDragThresholdExceeded: (direction: SlideDirection) => boolean;
}

export function useSlideGesture({
  enabled,
  sliderWidth,
  dragThreshold,
  onDrag,
  onDragThresholdExceeded,
}: SlideGestureProps): () => React.DOMAttributes<HTMLElement> {
  const startMovement = React.useRef(0);

  return useDrag(
    React.useCallback(
      ({ first, down, movement: [xMovement] }) => {
        if (!enabled) {
          return;
        }

        if (first) {
          startMovement.current = 0;
        }

        const dragMovement = startMovement.current - xMovement;

        onDrag(down ? dragMovement / sliderWidth : 0);

        if (down && Math.abs(dragMovement) > dragThreshold) {
          if (onDragThresholdExceeded(dragMovement < 0 ? 'right' : 'left')) {
            startMovement.current = xMovement;
          }
        }
      },
      [enabled, sliderWidth, dragThreshold, onDrag, onDragThresholdExceeded]
    ),
    { axis: 'x' }
  );
}
